module.exports = function setupUtils(app) {
    const opts = app.opts;
    const router = app.router;
    const createServer = require('../../server');
    let Sentry = app.sentry;
    let faker = app.faker;
    let sanitizeHtml = app.sanitizeHtml;
    let wsRedisPub = app.wsRedisPub;

    [
        'apiKey',
        'basicAuth',
        'authorize',
        'requirePermission',
        'rateLimit',
        'session',
        'csrf',
        'i18n',
        'redisCache',
        'csp',
        'htmx',
        'routeLocalization'
    ].forEach((name) => {
        Object.defineProperty(app, name, {
            get() {
                return require('../middlewares/' + name + '.js');
            }
        });
    });

    Object.assign(app, {
        dbPoolStats() {
            if (!app.db) return { error: 'Database is not configured' };
            if (opts.db && opts.db.type === 'postgres') {
                return {
                    totalCount: app.db.totalCount,
                    idleCount: app.db.idleCount,
                    waitingCount: app.db.waitingCount
                };
            }
            if (opts.db && (opts.db.type === 'mysql' || opts.db.type === 'mariadb')) {
                const pool = app.db.pool || app.db;

                const internalPool = pool.pool || pool;
                if (internalPool && internalPool._allConnections) {
                    return {
                        totalCount: internalPool._allConnections.length,
                        idleCount: internalPool._freeConnections.length,
                        waitingCount: internalPool._connectionQueue ? internalPool._connectionQueue.length : 0
                    };
                }
            }
            return { message: 'Pool stats not supported for this database type' };
        },

        sanitize(html, options) {
            if (!sanitizeHtml) throw new Error('Please install sanitize-html to use sanitize()');
            return sanitizeHtml(html, options);
        },

        metaTags(tags = {}) {
            let html = '';
            for (const [key, value] of Object.entries(tags)) {
                if (!value) continue;
                if (key === 'title') {
                    html += `<title>${value}</title>\n`;
                } else if (key.startsWith('og:') || key.startsWith('twitter:')) {
                    html += `<meta property="${key}" content="${value}">\n`;
                } else {
                    html += `<meta name="${key}" content="${value}">\n`;
                }
            }
            return html;
        },

        sentry: Sentry,

        faker,

        wsBroadcast(data) {
            const payload = typeof data === 'string' ? data : JSON.stringify(data);
            if (wsRedisPub && wsRedisPub.isOpen) {
                const channel = opts.ws.pubSubChannel || 'fullstx_ws_pubsub';
                wsRedisPub.publish(channel, payload).catch(console.error);
            } else if (this.wss) {
                this.wss.clients.forEach((client) => {
                    if (client.readyState === 1) client.send(payload);
                });
            }
        },

        factory(count, generatorFn) {
            if (!faker) throw new Error('Please install @faker-js/faker to use factory()');
            const items = [];
            for (let i = 0; i < count; i++) {
                items.push(generatorFn(faker, i));
            }
            return items;
        },

        zod: require('zod'),

        container: {
            services: new Map(),
            register(name, instance) {
                this.services.set(name, instance);
            },
            get(name) {
                if (!this.services.has(name)) {
                    throw new Error(`Service ${name} not found in IoC container`);
                }
                return this.services.get(name);
            }
        },

        cache: new Map(),

        upload(options = { dest: 'uploads/' }) {
            const multer = require('multer');
            return multer(options);
        },

        knex(config) {
            const knexLib = require('knex');
            return knexLib(config);
        },

        queue(name, connection) {
            const { Queue } = require('bullmq');
            return new Queue(name, { connection });
        },

        worker(name, processor, connection) {
            const { Worker } = require('bullmq');
            return new Worker(name, processor, { connection });
        },

        swagger(docPath = '/api-docs', swaggerDoc = {}) {
            const swaggerUiDistPath = require('swagger-ui-dist').getAbsoluteFSPath();
            const fs = require('fs');
            const path = require('path');

            router.get(docPath + '/swagger.json', (req, res) => {
                if (res.json) return res.json(swaggerDoc);
                res.writeHead(200, { 'Content-Type': 'application/json' });
                res.end(JSON.stringify(swaggerDoc));
            });

            router.use((req, res, next) => {
                if (req.url.startsWith(docPath + '/assets/')) {
                    const filePath = req.url.replace(docPath + '/assets/', '');
                    const absPath = path.join(swaggerUiDistPath, filePath);

                    if (absPath.startsWith(swaggerUiDistPath) && fs.existsSync(absPath)) {
                        const content = fs.readFileSync(absPath);
                        const ext = path.extname(absPath);
                        const mimes = { '.css': 'text/css', '.js': 'application/javascript', '.png': 'image/png' };
                        res.writeHead(200, { 'Content-Type': mimes[ext] || 'text/plain' });
                        return res.end(content);
                    }
                }
                next();
            });

            router.get(docPath, (req, res) => {
                const html = `
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8">
    <title>Swagger UI</title>
    <link rel="stylesheet" type="text/css" href="${docPath}/assets/swagger-ui.css" />
    <link rel="stylesheet" type="text/css" href="${docPath}/assets/index.css" />
    <link rel="icon" type="image/png" href="${docPath}/assets/favicon-32x32.png" />
  </head>
  <body>
    <div id="swagger-ui"></div>
    <script src="${docPath}/assets/swagger-ui-bundle.js"></script>
    <script src="${docPath}/assets/swagger-ui-standalone-preset.js"></script>
    <script>
      window.onload = function() {
        window.ui = SwaggerUIBundle({
          url: "${docPath}/swagger.json",
          dom_id: '#swagger-ui',
          deepLinking: true,
          presets: [SwaggerUIBundle.presets.apis, SwaggerUIStandalonePreset],
          plugins: [SwaggerUIBundle.plugins.DownloadUrl],
          layout: "StandaloneLayout"
        });
      };
    </script>
  </body>
</html>`;
                if (res.html) return res.html(html);
                res.writeHead(200, { 'Content-Type': 'text/html' });
                res.end(html);
            });
        },

        mailer(config) {
            const nodemailer = require('nodemailer');
            return nodemailer.createTransport(config);
        },

        cdn(url, cdnBaseUrl = opts.cdn) {
            if (!cdnBaseUrl) return url;
            const base = cdnBaseUrl.endsWith('/') ? cdnBaseUrl.slice(0, -1) : cdnBaseUrl;
            const path = url.startsWith('/') ? url : '/' + url;
            return base + path;
        },

        sitemap(urls = [], hostname = 'http://localhost') {
            return (req, res) => {
                let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
                xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
                for (const url of urls) {
                    const loc = typeof url === 'string' ? { url } : url;
                    xml += '  <url>\n';
                    xml += `    <loc>${hostname}${loc.url.startsWith('/') ? loc.url : '/' + loc.url}</loc>\n`;
                    if (loc.lastmod) xml += `    <lastmod>${loc.lastmod}</lastmod>\n`;
                    if (loc.changefreq) xml += `    <changefreq>${loc.changefreq}</changefreq>\n`;
                    if (loc.priority) xml += `    <priority>${loc.priority}</priority>\n`;
                    xml += '  </url>\n';
                }
                xml += '</urlset>';
                res.writeHead(200, { 'Content-Type': 'application/xml' });
                res.end(xml);
            };
        },

        featureFlags: new Map(),
        setFeature(name, isEnabled) {
            this.featureFlags.set(name, isEnabled);
        },
        isFeatureEnabled(name) {
            return !!this.featureFlags.get(name);
        },
        requireFeature(name) {
            return (req, res, next) => {
                if (!this.isFeatureEnabled(name)) {
                    if (res.json) return res.json({ error: `Feature '${name}' is disabled or not found.` }, 404);
                    res.writeHead(404, { 'Content-Type': 'application/json' });
                    return res.end(JSON.stringify({ error: `Feature '${name}' is disabled or not found.` }));
                }
                next();
            };
        },

        async loadTest(options = {}) {
            let autocannon;
            try {
                autocannon = require('autocannon');
            } catch {
                console.error("[!] Please install 'autocannon' to use loadTest()");
                return;
            }
            const config = Object.assign(
                {
                    url: 'http://localhost:3000',
                    connections: 10,
                    pipelining: 1,
                    duration: 10
                },
                options
            );
            console.log(`[fullstx] Starting load test on ${config.url} for ${config.duration}s...`);
            const result = await autocannon(config);
            console.log(autocannon.printResult(result));
            return result;
        },

        edgeCache({ maxAge = 60, swr = 300 } = {}) {
            return (req, res, next) => {
                if (req.method === 'GET') {
                    res.setHeader('Cache-Control', `public, max-age=${maxAge}, stale-while-revalidate=${swr}`);
                }
                next();
            };
        },

        queueDashboard(queues = [], endpoint = '/admin/queues') {
            router.get(endpoint, async (req, res) => {
                const stats = [];
                for (const q of queues) {
                    try {
                        const [waiting, active, completed, failed, delayed] = await Promise.all([
                            q.getWaitingCount(),
                            q.getActiveCount(),
                            q.getCompletedCount(),
                            q.getFailedCount(),
                            q.getDelayedCount()
                        ]);
                        stats.push({ name: q.name, waiting, active, completed, failed, delayed });
                    } catch {
                        stats.push({ name: q.name, error: 'Could not fetch stats' });
                    }
                }
                if (res.json) return res.json({ queues: stats });
                res.writeHead(200, { 'Content-Type': 'application/json' });
                res.end(JSON.stringify({ queues: stats }));
            });
        },

        exportHandler() {
            const server = createServer(router.routes, router.middlewares, { staticDir: opts.staticDir });
            return (req, res) => {
                server.emit('request', req, res);
            };
        },

        graphql(endpoint = '/graphql', schema, rootValue) {
            let createHandler;
            try {
                createHandler = require('graphql-http/lib/use/http').createHandler;
            } catch {
                console.error("[!] Please install 'graphql' and 'graphql-http' to use graphql()");
                return;
            }
            const handler = createHandler({ schema, rootValue });
            router.use((req, res, next) => {
                if (req.url.startsWith(endpoint) && (req.method === 'GET' || req.method === 'POST')) {
                    return handler(req, res);
                }
                next();
            });
        },

        twoFactor() {
            let otplib, qrcode;
            try {
                otplib = require('otplib');
                qrcode = require('qrcode');
            } catch {
                console.error("[!] Please install 'otplib' and 'qrcode' to use twoFactor()");
                return {};
            }
            return {
                generateSecret(user = 'user', service = 'fullstx') {
                    const secret = otplib.authenticator.generateSecret();
                    const otpauth = otplib.authenticator.keyuri(user, service, secret);
                    return { secret, otpauth };
                },
                async generateQRCode(otpauth) {
                    return await qrcode.toDataURL(otpauth);
                },
                verify(token, secret) {
                    return otplib.authenticator.check(token, secret);
                }
            };
        },

        grpc() {
            let grpcJs, protoLoader;
            try {
                grpcJs = require('@grpc/grpc-js');
                protoLoader = require('@grpc/proto-loader');
            } catch {
                console.error("[!] Please install '@grpc/grpc-js' and '@grpc/proto-loader' to use gRPC.");
                return {};
            }
            return {
                loadProto(
                    protoPath,
                    options = { keepCase: true, longs: String, enums: String, defaults: true, oneofs: true }
                ) {
                    const packageDefinition = protoLoader.loadSync(protoPath, options);
                    return grpcJs.loadPackageDefinition(packageDefinition);
                },
                createServer() {
                    return new grpcJs.Server();
                },
                ServerCredentials: grpcJs.ServerCredentials,
                credentials: grpcJs.credentials
            };
        }
    });
};
